import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component as ContentfulComponent} from '@amblique/king-living/app/components/contentful/component';

/**
 * @override
 * ActionLinks component from Contentful
 * @param {Object} props - Component props
 * @param {Contentful.ActionLinks} props.component - ActionLinks component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement}
 */
export const ActionLinks = ({component, isComplete = true, isFetching = false, ...props}) => {
  if (!isComplete && isFetching) {
    return null;
  }

  const {description, linksCollection, displayStyle} = component;
  const styles = useMultiStyleConfig('contentful/action-links', {component});

  const links = (linksCollection?.items ?? []).filter((link) => link?.sys?.id);

  if (links.length === 0 && !description) {
    return null;
  }

  // Determine flex direction based on displayStyle
  // Default to 'horizontal' (row) if displayStyle is unset
  const direction = !displayStyle || displayStyle === 'horizontal' ? 'row' : 'column';

  return (
    <Box {...styles.root} {...props}>
      {description && (
        <Text {...styles.description}>{description}</Text>
      )}

      {links.length > 0 && (
        <Stack direction={direction} {...styles.links}>
          {links.map((link) => (
            <ContentfulComponent
              key={link.sys.id}
              component={link}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

ActionLinks.displayName = 'ActionLinks';
ActionLinks.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
